// src/components/Reports/widgets/StatisticsWidget.tsx

import React, { useEffect, useState, useRef } from 'react';
import { MoreVertical } from 'lucide-react';
import DashboardReportService from '@services/DashboardReportService';
import type {
  ReportWidget,
  ReportFilters,
  StatisticsData,
  StatisticsItem,
  NewSettings,
} from '@services/DashboardReportService';
import { Modal } from '@/components/utils/Modal';
import StatisticsSettings from './StatisticsWidgetSettings';

interface StatisticsWidgetProps {
  widget: ReportWidget;
  dashboard: { DashboardId: number; ReportId: number };
  filters: ReportFilters;
  onRemoveWidget: (widget: ReportWidget) => void;
  onUpdateSettings?: (widgetId: number, newSettings: NewSettings) => void;
}

function normalizeIcon(icon: string): string
{
  return icon.replace(/"/g, '').toLowerCase().trim();
}

function Capsule({ percentage }: { percentage: number })
{
  const pct = Math.min(100, Math.max(0, percentage));
  return (
    <span className="team-capsule">
      <span className="inner" style={{ width: `${pct}%` }} />
    </span>
  );
}

function StatItem({ item }: { item: StatisticsItem & { showPercentage?: boolean } })
{
  const iconClass = `identifier-icon-key ${normalizeIcon(item.Icon)}`;
  const displayValue = item.showPercentage ? `${item.Percentage}%` : item.Total;
  const showBar = item.ConfigValueId !== 9 && item.ConfigValueId !== 1;

  return (
    <li>
      <div className="statistics-item-layout type-A">
        <div className="statistics-item-icon-cell">
          <span className={iconClass} />
        </div>
        <div className="statistics-item-data-cell">
          <div className="statistics-item-figure-cell">
            <span>{displayValue}</span>
          </div>
          {showBar && <Capsule percentage={item.Percentage} />}
        </div>
      </div>
    </li>
  );
}

export const StatisticsWidget: React.FC<StatisticsWidgetProps> = ({
  widget,
  dashboard,
  filters,
  onRemoveWidget,
  onUpdateSettings,
}) =>
{
  const settings = widget.DataValuesJson?.Settings ?? {};

  const [data, setData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() =>
  {
    let cancelled = false;
    loadData(cancelled);
    return () =>
    {
      cancelled = true;
    };
  }, [dashboard.DashboardId, widget.WidgetId, filters]);

  const loadData = async (cancelled?: boolean) =>
  {
    try
    {
      setLoading(true);
      setError(null);
      const result = await DashboardReportService.getReportStatistics({
        dashboardId: dashboard.DashboardId,
        widgetId: widget.WidgetId,
        filters,
      });
      if (!cancelled) setData(result);
    }
    catch
    {
      if (!cancelled) setError('Failed to load statistics.');
    }
    finally
    {
      if (!cancelled) setLoading(false);
    }
  };

  useEffect(() =>
  {
    const handleClick = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Get a statistic item by Name from Statistics array
  const getStatByName = (name: string): (StatisticsItem & { showPercentage: boolean }) | null =>
  {
    const item = data?.Statistics.find(s => s.Name === name);
    if (!item) return null;
    return { ...item, showPercentage: true };
  };

  // Global counters — all Statistics items (excluding ProjectCalendar and ProjectRemaining displayed separately)
  const globalCounters = (data?.Statistics ?? []).filter(
    s => s.Name !== 'ProjectCalendar' && s.Name !== 'ProjectRemaining',
  );

  const handleDelete = async () =>
  {
    setMenuOpen(false);
    setDeleteModal(true);
  };

  const deleteWidget = async () =>
  {
    setMenuOpen(false);
    try
    {
      await DashboardReportService.deleteWidget(widget.ReportId, widget.WidgetId);
      onRemoveWidget(widget);
    }
    catch
    {
      // ignore
    }
  };

  const getDataChartNewSettings = (
    _data: unknown,
    _selectedLayer: unknown,
    request: {
      LayerId?: number;
      DateFieldId?: number;
      AddDynamicGroup?: boolean;
      CardType?: string;
      ConfigValueID?: number;
    },
  ) =>
  {
    const newSettings: NewSettings = {
      LayerId: request.LayerId,
      DateFieldId: request.DateFieldId,
      AddDynamicGroup: request.AddDynamicGroup,
      CardType: request.CardType,
      ConfigValueID: request.ConfigValueID,
    };

    setShowSettings(false);
    setLoading(false);
    if (onUpdateSettings) onUpdateSettings(widget.WidgetId, newSettings);
    loadData();
  };

  const projectRemaining = getStatByName('ProjectRemaining');
  const projectCalendar = getStatByName('ProjectCalendar');

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <>
      <div className="styled-widget-container">
        {/* Widget Header */}
        <div className="widget-header">
          <span className="text-sm font-medium text-gray-700 dark:text-[#a9a093]">
            {widget.Description || 'Card Statistics'}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-[#3a3c3c] rounded dark:text-[#a9a093]"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Widget menu"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white dark:bg-[#333535] border border-gray-200 dark:border-[#4a4c4c] rounded shadow-md min-w-[140px]">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093] disabled:cursor-not-allowed"
                  onClick={() =>
                  {
                    setMenuOpen(false);
                    setShowSettings(true);
                  }}
                >
                  Settings
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] text-red-600"
                  onClick={handleDelete}
                >
                  Delete Widget
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Widget Content */}
        <div className="widget-content">
          {loading && (
            <p className="report-widget-feedback-message text-center">Loading statistics...</p>
          )}
          {error && (
            <p className="report-widget-feedback-message" style={{ color: '#c00' }}>
              {error}
            </p>
          )}

          {!showSettings && !loading && !error && data && (
            <div className="statistics">
              {/* Horizontal list: ProjectRemaining | ProjectCalendar | global counters */}
              <ol className="list">
                {projectRemaining && <StatItem item={projectRemaining} />}
                {projectCalendar && <StatItem item={projectCalendar} />}
                {globalCounters.map(item => (
                  <StatItem
                    key={`${item.Name}-${item.ConfigValueId}`}
                    item={{ ...item, showPercentage: false }}
                  />
                ))}
              </ol>

              {/* See more / See less toggle */}
              <div style={{ fontSize: 11, marginTop: 4 }}>
                <a
                  href="#"
                  onClick={e =>
                  {
                    e.preventDefault();
                    setShowDetails(v => !v);
                  }}
                  style={{ color: isDark ? '#62accb' : '#337ab7' }}
                >
                  {showDetails ? 'See less' : 'See more'}
                </a>
              </div>

              {/* Per-layer breakdown */}
              {showDetails && (
                <div
                  style={{
                    borderTop: `1px solid ${isDark ? '#4a4c4c' : '#f0f0f0'}`,
                    marginTop: 8,
                    paddingTop: 8,
                  }}
                >
                  {data.Layers.map(layer =>
                  {
                    const layerItems = data.StatisticsDetails.filter(
                      d => d.LayerId === layer.LayerId,
                    );
                    if (layerItems.length === 0) return null;
                    return (
                      <div
                        key={layer.LayerId}
                        className="layer-color-wrapper"
                        style={{ borderTop: `3px solid ${layer.Color}` }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: layer.Color,
                            marginBottom: 6,
                          }}
                        >
                          {layer.Name}
                        </div>
                        <ol className="list">
                          {layerItems.map(item => (
                            <StatItem
                              key={`${layer.LayerId}-${item.Name}-${item.ConfigValueId}`}
                              item={{ ...item, showPercentage: false }}
                            />
                          ))}
                        </ol>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {showSettings && (
            <StatisticsSettings
              widgetId={widget.WidgetId}
              dashboard={dashboard}
              settings={settings}
              onCancel={() => setShowSettings(false)}
              onSave={getDataChartNewSettings}
            />
          )}
        </div>
      </div>
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Widget"
        preventCloseClickOut
      >
        <p>Are you sure you want to delete it?</p>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() =>
            {
              deleteWidget();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
            Ok
          </button>
          <button
            type="button"
            onClick={() => setDeleteModal(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default StatisticsWidget;
